import type { Routine as RcRoutine } from '@sb/remote-control/types';

import type { Diagnostic, ValidationRule } from '../../types';
import { diag } from '../../util/ruleHelpers';

/**
 * Checks whether a step has been configured (i.e. has non-falsy `args`)
 * by inspecting the *raw* (pre-normalization) routine stored in
 * `ctx.rawRoutine`. After normalization `ensureArgumentKinds` always
 * synthesises args, so the normalised view can never detect this.
 *
 * `ctx.rawRoutine` is threaded automatically by `precheckForExecution`.
 * Direct rule-runner callers must include it themselves, otherwise this
 * rule no-ops via the `rawRoutine == null` guard.
 *
 * Emits warnings only, so `precheckForExecution` filters them out of its
 * blocking-errors result.
 */
export const StepConfiguredValidation: ValidationRule = {
  id: 'steps.StepConfigured',
  appliesTo: 'step',
  run: ({ routine, ctx, stepId }): Diagnostic[] => {
    if (!stepId) return [];
    if (!routine.steps.some((s) => s.id === stepId)) return [];

    const rawRoutine = ctx.rawRoutine as
      | RcRoutine.ConvertedResponse
      | undefined;

    if (!rawRoutine) return [];

    const rawStepCfg = rawRoutine.stepConfigurations?.[stepId];

    if (rawStepCfg?.args) return [];

    return [
      diag({
        code: 'ui.missingConfiguration',
        message: 'This step needs to be configured.',
        stepId,
        sourceRule: 'steps.StepConfigured',
        severity: 'warning',
      }),
    ];
  },
};
